import { Dispatch, SetStateAction, useCallback, useContext, useMemo, useState } from 'react';
import { permissions, availableRoles, customerAvailableRoles } from '@/constants';
import InputError from '@/components/InputError';
import SectionBorder from '@/components/SectionBorder';
import { Box, Button, Grid, MenuItem, Divider, Typography, IconButton, Autocomplete, styled, TextField } from '@mui/material';
import { updateTeamMemberSchema } from '@/lib/validation/team';
import { FormikHelpers } from 'formik';
import MyFormik from '@/components/Formik';
import type { TeamMemberUpdateInput } from '@/types/formInput';
import type { User } from '@/types/api';
import { useGetVehiclesByCompany } from '@/hooks/customer/useVehicles';
import { PTextField, SnackBarContext } from 'pragmatic-ui';
import SaveIcon from '@/components/icons/SaveIcon';
import { useTeamMemberUpdate } from '@/hooks/admin/useTeam';
import ConfirmationPopup from '@/components/ConfirmationPopup';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CloseIcon from '@mui/icons-material/Close';
import {
  useUpdateUser,
  useDeleteUser,
} from '@/hooks/admin/useUsers';
import { SelectOption } from '@/types/types';

interface Props {
  setDrawerOpen: Dispatch<SetStateAction<boolean>>;
  selectedUser: User | null;
}

const isSelectedUserMembership = false;

const StyledAutocomplete = styled(Autocomplete)({
  '& .MuiChip-root': {
    color: '#F6AE2D',
    border: '1px solid #F6AE2D',
    background: 'transparent',
    '& .MuiSvgIcon-root': {
      color: '#F6AE2D !important',
    },
  },
});

const StyledForm = styled('form')(() => ({
  width: '100%',
}));

export default function TeamMemberManager({
  setDrawerOpen,
  selectedUser: user,
}: Props) {
  const { showSnack } = useContext(SnackBarContext);
  const isAdmin = user?.type === 'admin';
  const teamMemberUpdate = useTeamMemberUpdate();
  const updateUser = useUpdateUser();
  const deleteUser = useDeleteUser();
  const [confirmPopupDeleteAccount, setConfirmPopupDeleteAccount] =
    useState(false);

  const { data: vehiclesByCompany = [] } = user?.currentCompany?._id ? useGetVehiclesByCompany(user?.currentCompany?._id) : { data: [] };

  const vehicleOptionsAll = useMemo(() => (vehiclesByCompany.filter(vehicle => vehicle._id && vehicle.rego).map(vehicle => ({
    id: vehicle._id,
    name: vehicle.rego,
  }))), [vehiclesByCompany])

  const allAvailableRoles = [...availableRoles, ...customerAvailableRoles];

  /**
   * handleSubmit
   */
  const handleSubmit = (
    values: TeamMemberUpdateInput,
    { resetForm }: FormikHelpers<TeamMemberUpdateInput>,
  ) => {
    if (!user) return;

    // @ts-expect-error - typescript bug
    teamMemberUpdate.mutate(values);
    resetForm();
    setDrawerOpen(false);

    const formData = new FormData();

    const selectedRole = allAvailableRoles.find(
      (role) => role.type === values.role,
    );
    if (!selectedRole) {
      showSnack('Error creating customer user', 'error');
      return;
    }

    const vehicleIds = values.vehicles.map(vehicle => vehicle.id).join(',');

    formData.append('_id', user._id);
    formData.append('name', values.name);
    formData.append('email', values.email);
    formData.append('role', selectedRole.type ?? user.type);
    formData.append('mobile', values.mobile);
    formData.append('vehicles', vehicleIds);

    updateUser.mutate(formData, {
      onSuccess: () => {
        resetForm();
        setDrawerOpen(false);
      },
    });
  };

  /**
   * initialValues
   */
  const initialValues = useMemo(() => {
    const filtered = vehicleOptionsAll.filter(option => user?.vehicles?.some(vehicle => vehicle._id === option.id));
    const vehicles = filtered.map((v) => ({ id: v.id, name: v.name })) || []

    return {
      name: user?.name || '',
      email: user?.email || '',
      role: user?.type || allAvailableRoles[0].type,
      mobile: user?.mobile || '',
      vehicles: vehicles, // For customer
    }
  }, [vehicleOptionsAll])

  /**
   * formikProps
   */
  const formikProps = useMemo(() => ({
    initialValues: initialValues,
    validationSchema: updateTeamMemberSchema,
    onSubmit: handleSubmit,
    enableReinitialize: true, // Note: Allow Formik to re-render
  }), [initialValues, handleSubmit]);

  /**
   * resetForm
   */
  const resetFormToInitial = useCallback(() => {
    formikProps.initialValues = initialValues;
  }, [formikProps, initialValues])

  /**
   * handleDeleteAction
   */
  const handleDeleteAction = useCallback(() => {
    // Add your delete logic here
    deleteUser.mutate(user!._id, {
      onSuccess: () => {
        setConfirmPopupDeleteAccount(false);
        resetFormToInitial();
        setDrawerOpen(false);
        showSnack('Successfully deleted user', 'success');
      },
    });
  }, [resetFormToInitial]);

  return (
    <>
      {permissions.canAddTeamMembers ? (
        <>
          <SectionBorder />

          <MyFormik<TeamMemberUpdateInput> {...formikProps}>
            {({ handleSubmit, setFieldValue, values, errors, resetForm }) => {
              return (
                <StyledForm onSubmit={handleSubmit}>

                  {/* <!-- Buttons --> */}
                  <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                    m={2}
                  >
                    <Typography variant="h6" sx={{ color: '#fff' }}>
                      <ArrowBackIcon
                        sx={{ cursor: 'pointer', mr: 2, fill: '#fff', transform: 'translateY(5px)' }}
                        onClick={() => setDrawerOpen(false)}
                      />Edit User</Typography>

                    <Box display="flex" justifyContent="flex-end" sx={{}}>
                      <Button
                        sx={{ mr: 1 }}
                        size="small"
                        variant="outlined"
                        onClick={() => {
                          setConfirmPopupDeleteAccount(true);
                          // resetForm();
                          // setDrawerOpen(false);
                        }}
                      >
                        DELETE
                      </Button>
                      <Button
                        sx={{ mr: 1 }}
                        type="submit"
                        size="small"
                        variant="contained"
                        startIcon={<SaveIcon />}
                      >
                        SAVE
                      </Button>
                      <IconButton
                        size="small"
                        onClick={() => {
                          resetForm();
                          setDrawerOpen(false);
                        }}
                      >
                        <CloseIcon />
                      </IconButton>
                    </Box>
                  </Box>

                  <Divider />

                  {/* <!-- Form Fields --> */}
                  <Box mt={2} ml={5} mr={5}>
                    <Grid container>
                      <Grid item xs={12}>
                        <PTextField
                          name="role"
                          label="Role"
                          variant="standard"
                          className="config-text-fields"
                          fullWidth={true}
                          sx={{
                            mr: 4,
                            mt: 1,
                            minWidth: '150px',
                          }}
                          InputProps={{
                            disableUnderline: false,
                          }}
                          select
                          SelectProps={{
                            multiple: false,
                            sx: {
                              '& .MuiSvgIcon-root': {
                                color: '#F6AE2D',
                              },
                            },
                          }}
                        >
                          {allAvailableRoles.map((role) => (
                            <MenuItem
                              key={role.type}
                              value={role.type}
                            >
                              {role.label}
                            </MenuItem>
                          ))}
                        </PTextField>
                        <InputError message={errors.role} sx={{ mt: 1 }} />
                      </Grid>
                      <Grid item xs={12}>
                        <Box component="div">
                          <PTextField
                            variant="standard"
                            name="name"
                            type="name"
                            disabled={isSelectedUserMembership}
                            placeholder="Name"
                            InputProps={{
                              disableUnderline: false,
                              sx: { fontSize: '15px' },
                            }}
                            fullWidth
                          />
                          <InputError message={errors.name} sx={{ mt: 1 }} />
                        </Box>
                      </Grid>
                      <Grid item xs={12}>
                        <Box component="div">
                          <PTextField
                            variant="standard"
                            name="email"
                            type="email"
                            disabled={isSelectedUserMembership}
                            placeholder="Email"
                            InputProps={{
                              disableUnderline: false,
                              sx: { fontSize: '15px' },
                            }}
                            fullWidth
                          />
                          <InputError message={errors.email} sx={{ mt: 1 }} />
                        </Box>
                      </Grid>
                      <Grid item xs={12}>
                        <Box component="div">
                          <PTextField
                            variant="standard"
                            name="mobile"
                            type="mobile"
                            placeholder="Mobile #"
                            InputProps={{
                              disableUnderline: false,
                              sx: { fontSize: '15px' },
                            }}
                            fullWidth
                          />
                          <InputError message={errors.email} sx={{ mt: 1 }} />
                        </Box>
                      </Grid>

                      {
                        (!isAdmin) && (
                          <Grid item xs={12}>
                            <StyledAutocomplete
                              id="combo-box-vehicles"
                              options={vehicleOptionsAll}
                              defaultValue={initialValues['vehicles']}
                              multiple
                              fullWidth
                              isOptionEqualToValue={(option: any, value: any) => option.id === value.id}
                              getOptionLabel={(option: any) => {
                                if (typeof option === 'string') {
                                  return option;
                                }

                                return option.name;
                              }}
                              renderInput={(params) => (
                                <TextField
                                  {...params}
                                  fullWidth
                                  variant="standard"
                                  placeholder="Assign Vehicles"
                                />
                              )}
                              onChange={(_, vehicles: any) => {
                                if (initialValues.vehicles === vehicles) return;
                                setFieldValue('vehicles', vehicles);
                              }}
                              filterOptions={(options) => (options as unknown[] as SelectOption[]).filter(option => !values.vehicles.some(vehicle => vehicle.id === option.id))
                              }
                              selectOnFocus
                              handleHomeEndKeys
                            />
                          </Grid>
                        )
                      }
                      <Box display="flex" justifyContent="flex-end" sx={{ mt: 8 }}>
                      </Box>
                    </Grid>
                  </Box>
                </StyledForm>
              )
            }}
          </MyFormik>
        </>
      ) : null}
      <ConfirmationPopup
        open={confirmPopupDeleteAccount}
        title="Delete Account"
        message={<>Are you sure you want to delete this account?</>}
        handleConfirmClick={handleDeleteAction}
        handleCloseClick={() => {
          setConfirmPopupDeleteAccount(false);
        }}
      />
    </>
  );
}
